import { useEffect } from 'react';
import { InputNumber, Segmented } from 'antd';
import type { PaymentMethod } from '../types';
import { formatVND } from '@/utils/format';
import { MONEY_MAX, moneyFormatter, moneyParser, moneyInputKeyDownGuard } from '../utils/moneyInput';
import { NonCashPaymentSection } from './NonCashPaymentSection';
import { useAvailablePaymentMethods } from '../hooks/useAvailablePaymentMethods';
import type { ShippingMode } from '../utils/shippingTotals';

export interface PaymentMethodFieldsProps {
  paymentMethod: PaymentMethod;
  onPaymentMethodChange: (m: PaymentMethod) => void;
  /**
   * Số tiền phải thu (grandTotal đã trừ voucher). Component KHÔNG tự tính —
   * nhận từ ngoài để mọi khối tiền ở cột phải (`OrderInfoPanel`, khối này,
   * `ShippingTotals`) luôn dùng chung một con số.
   */
  payable: number;
  cashReceived?: number;
  onCashReceivedChange: (v: number) => void;
  paymentAccountId?: string;
  onPaymentAccountChange: (id: string | undefined) => void;
  nonCashAmount?: number;
  onNonCashAmountChange: (v: number | undefined) => void;
  /**
   * Có mặt ⇒ đơn đang ở chế độ giao hàng. Khi đó số tiền quầy thu là "Khách trả
   * trước", do `OrderInfoPanel` nhận; component này chỉ còn phần chọn
   * hình thức thanh toán, KHÔNG render bảng "Tiền khách đưa / Tiền thừa" (số
   * thối lại tính trên `payable` sẽ sai với đơn có COD).
   */
  shippingMode?: ShippingMode;
}

const METHOD_LABEL: Record<PaymentMethod, string> = {
  cash: 'Tiền mặt',
  bank: 'CK',
  wallet: 'Ví ĐT',
  card: 'Thẻ TD',
};

export function PaymentMethodFields({
  paymentMethod,
  onPaymentMethodChange,
  payable,
  cashReceived,
  onCashReceivedChange,
  paymentAccountId,
  onPaymentAccountChange,
  nonCashAmount,
  onNonCashAmountChange,
  shippingMode,
}: PaymentMethodFieldsProps) {
  const { methods: availableMethods, isLoading: methodsLoading } = useAvailablePaymentMethods();

  // Nếu method đang chọn không còn khả dụng (tài khoản bị tắt/xoá, hoặc khôi phục
  // tab cũ từ localStorage) → về cash. Chờ load xong để tránh reset nhầm.
  useEffect(() => {
    if (methodsLoading) return;
    if (!availableMethods.includes(paymentMethod)) {
      onPaymentMethodChange('cash');
    }
  }, [methodsLoading, availableMethods, paymentMethod, onPaymentMethodChange]);

  const methodOptions = availableMethods.map((m) => ({ value: m, label: METHOD_LABEL[m] }));
  const showMethodPicker = availableMethods.length > 1;

  const isCash = paymentMethod === 'cash';
  const change = Math.max(0, (cashReceived ?? 0) - payable);

  const hasShipping = !!shippingMode;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* LUÔN hiện khi tenant có nhiều hơn một hình thức. Trước đây đơn giao
          hàng có "Khách trả trước" = 0 thì khối này bị ẩn, khiến thu ngân định
          thu trước một phần lại không thấy chỗ chọn hình thức — phải nhập số
          rồi khối mới hiện ra. */}
      {showMethodPicker && (
        <div>
          <div
            className="pos-payment-method-label"
            style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: '#333' }}
          >
            Hình thức thanh toán
          </div>
          <Segmented
            block
            className="pos-segmented-selected-red"
            value={paymentMethod}
            onChange={(v) => onPaymentMethodChange(v as PaymentMethod)}
            options={methodOptions}
          />
        </div>
      )}

      {!hasShipping &&
        (isCash ? (
          <table className="pos-payment-cash-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <tbody>
              <tr>
                <td style={{ color: '#555', paddingBottom: 8, width: '45%' }}>Tiền khách đưa:</td>
                <td style={{ paddingBottom: 8 }}>
                  {/* Cùng khuôn với ô "Phụ thu" của `OrderInfoPanel`: gạch chân,
                      size small, số căn phải, không stepper (mũi tên antd nổi đè
                      lên chữ số căn phải khi hover). Hai khối này nằm sát nhau
                      trong cùng cột phải nên phải cùng một kiểu ô, nếu không đơn
                      lấy tại chi nhánh có đúng một ô viền hộp giữa rừng ô gạch
                      chân. `pos-money-input-right` lo phần căn phải — xem
                      `POSPage.css`. */}
                  <InputNumber
                    aria-label="Tiền khách đưa"
                    className="pos-money-input-right"
                    variant="underlined"
                    size="small"
                    controls={false}
                    style={{ width: '100%' }}
                    min={0}
                    max={MONEY_MAX}
                    value={cashReceived}
                    placeholder="0"
                    formatter={moneyFormatter}
                    parser={moneyParser}
                    onKeyDown={moneyInputKeyDownGuard}
                    onChange={(v) => onCashReceivedChange(v ?? 0)}
                  />
                </td>
              </tr>
              <tr>
                <td style={{ color: '#555' }}>Tiền thừa trả khách:</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: '#52c41a' }}>{formatVND(change)}</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <NonCashPaymentSection
            method={paymentMethod}
            amount={payable}
            accountId={paymentAccountId}
            onAccountChange={onPaymentAccountChange}
            nonCashAmount={nonCashAmount}
            onNonCashAmountChange={onNonCashAmountChange}
            cashReceived={cashReceived}
            onCashReceivedChange={onCashReceivedChange}
          />
        ))}
    </div>
  );
}
